import React from 'react';

// Cobertura mínima do dado de taxa pra mostrar o LÍQUIDO como número principal. Espelha
// utils/autoTune.MIN_FEE_COVERAGE_PCT no backend: a tela e o tuner precisam concordar sobre
// quando a taxa foi medida, senão o usuário lê um número e o robô decide por outro.
const MIN_FEE_COVERAGE_PCT = 90;

/**
 * KPIs primários da operação — a "leitura de 5 segundos" pra saber se a
 * estratégia está saudável.
 *
 * 🚨 Desde 09/08 o número em destaque é o LÍQUIDO DE TAXA, com o bruto embaixo. Até então
 * todo KPI daqui era bruto: a comissão só era descontada quando cobrada na moeda da cotação,
 * e esta conta paga em BNB. Na produção, 30 dias de bruto +0,20 eram, na verdade, −2,15.
 * Um win rate bruto não é conservador, é o número errado — trade que ganha menos que a
 * corretagem aparece como vitória.
 *
 * Quando a taxa NÃO foi medida na janela (testnet não registra comissão, ordem antiga sem o
 * campo, preço do BNB indisponível), a tela volta pro bruto e DIZ isso. O que não pode
 * acontecer é mostrar um "líquido" que é o bruto disfarçado.
 *
 * props:
 * - data: { trades, winRate, profitFactor, avgWin, avgLoss, maxDrawdown, tradesPerDay,
 *           winRateNet, profitFactorNet, avgWinNet, avgLossNet, netPnl, grossPnl,
 *           feeRoundTripPct, feeSummary } | null
 */
function HeadlineMetrics({ data }) {
    if (!data) {
        return (
            <div className='card card-body border-0 shadow mb-4 text-muted text-center py-3' style={{ fontSize: 14 }}>
                Nenhuma trade no período. Os KPIs aparecem aqui após pelo menos um par BUY→SELL fechado.
            </div>
        );
    }

    if (data.trades === 0) {
        return (
            <div className='card card-body border-0 shadow mb-4 text-muted text-center py-3' style={{ fontSize: 14 }}>
                Há ordens no período mas nenhum par BUY→SELL completou. Os KPIs aparecem quando você fechar pelo menos uma posição.
            </div>
        );
    }

    const fee = data.feeSummary || null;
    // "Medido" exige as duas coisas: o dado existir nas ordens E a moeda da comissão ter sido
    // resolvida. `coveragePct` já é o produto das duas no backend.
    const taxaMedida = !!fee && fee.coveragePct != null && fee.coveragePct >= MIN_FEE_COVERAGE_PCT;

    const winRate = taxaMedida && data.winRateNet != null ? data.winRateNet : data.winRate;
    const profitFactor = taxaMedida && data.profitFactorNet !== undefined ? data.profitFactorNet : data.profitFactor;
    const avgWin = taxaMedida && data.avgWinNet != null ? data.avgWinNet : data.avgWin;
    const avgLoss = taxaMedida && data.avgLossNet != null ? data.avgLossNet : data.avgLoss;

    const winRateColor = winRate >= 60 ? 'text-success' : winRate >= 40 ? 'text-warning' : 'text-danger';
    const pfColor = profitFactor == null ? 'text-muted'
        : profitFactor >= 1.5 ? 'text-success'
            : profitFactor >= 1.0 ? 'text-warning' : 'text-danger';
    const ddColor = data.maxDrawdown < 100 ? 'text-success' : data.maxDrawdown < 500 ? 'text-warning' : 'text-danger';

    const winLossRatio = (avgWin != null && avgLoss != null && avgLoss > 0)
        ? (avgWin / avgLoss).toFixed(2)
        : null;

    // Quanto a corretagem consumiu do resultado bruto. Acima de 100% o símbolo opera no vermelho
    // por construção, por mais que acerte a direção — é o número que diz se o tamanho da ordem
    // se paga, e o que motivou a revisão de nocional mínimo.
    const taxaTotal = fee ? fee.attributedUsdt : null;
    const taxaSobreBrutoPct = (taxaTotal != null && data.grossPnl > 0)
        ? (taxaTotal / data.grossPnl) * 100
        : null;

    return (
        <>
        {fee && !taxaMedida && (
            <div className='alert alert-warning border-0 shadow-sm mb-3 py-2' style={{ fontSize: 13 }}>
                <strong>Números brutos (sem taxa).</strong>{' '}
                {fee.verdict || 'A comissão não pôde ser medida nesta janela.'}{' '}
                Trades que ganham menos que a corretagem aparecem aqui como vitórias.
            </div>
        )}
        <div className='row g-3 mb-4'>
            <KpiCard
                label={taxaMedida ? 'Win Rate (líquido)' : 'Win Rate (bruto)'}
                value={`${winRate}%`}
                hint={taxaMedida
                    ? `${data.trades} ${data.trades === 1 ? 'trade' : 'trades'} · bruto ${data.winRate}%`
                    : `${data.trades} ${data.trades === 1 ? 'trade fechada' : 'trades fechadas'}`}
                cls={winRateColor}
                tooltip={taxaMedida
                    ? 'Percentual de pares BUY→SELL que sobraram positivos DEPOIS da corretagem das duas pernas. A diferença pro bruto são as trades que ganharam menos que a taxa. > 60% saudável; < 40% precisa revisar.'
                    : 'Percentual de pares BUY→SELL com lucro, SEM descontar a corretagem. > 60% saudável; < 40% precisa revisar.'}
            />
            <KpiCard
                label={taxaMedida ? 'Profit Factor (líquido)' : 'Profit Factor (bruto)'}
                value={profitFactor != null ? profitFactor.toFixed(2) : (avgWin != null ? '∞' : '—')}
                hint={taxaMedida && data.profitFactor != null
                    ? `lucro / |prejuízo| · bruto ${data.profitFactor.toFixed(2)}`
                    : 'lucro / |prejuízo|'}
                cls={pfColor}
                tooltip='Lucro dividido pelo prejuízo. > 1.5 = estratégia saudável; entre 1.0–1.5 = atenção; < 1.0 = perde dinheiro.'
            />
            {taxaMedida && (
                <KpiCard
                    label='Taxa paga'
                    value={`$${taxaTotal.toFixed(2)}`}
                    hint={taxaSobreBrutoPct != null
                        ? `${taxaSobreBrutoPct.toFixed(0)}% do lucro bruto`
                        : (data.feeRoundTripPct != null ? `${data.feeRoundTripPct}% por ida e volta` : '')}
                    cls={taxaSobreBrutoPct != null && taxaSobreBrutoPct >= 100 ? 'text-danger'
                        : taxaSobreBrutoPct != null && taxaSobreBrutoPct >= 50 ? 'text-warning' : 'text-muted'}
                    tooltip='Corretagem das trades fechadas no período. Comparada com o lucro BRUTO: acima de 100% a operação perde dinheiro mesmo acertando a direção — sinal de que o tamanho das ordens é pequeno demais pro passo que a estratégia captura.'
                />
            )}
            <KpiCard
                label='Max Drawdown'
                value={`$${data.maxDrawdown.toFixed(2)}`}
                hint='maior queda do lucro'
                cls={ddColor}
                tooltip='Drawdown = a maior queda do seu lucro acumulado, do topo ao fundo. Em miúdos: o pior tombo que a operação levou antes de recuperar. Quanto menor em relação ao ganho médio, melhor.'
            />
            <KpiCard
                label='Win/Loss'
                value={winLossRatio ? `${winLossRatio}x` : '—'}
                hint={avgWin != null && avgLoss != null
                    ? `+$${avgWin.toFixed(2)} / -$${avgLoss.toFixed(2)}`
                    : ''}
                cls='text-info'
                tooltip='Quanto cada ganho médio vale relativo a cada perda média. > 1 = ganhos > perdas em ticket; combinado com win rate baixo ainda pode ser lucrativo.'
            />
            <KpiCard
                label='Trades/dia'
                value={data.tradesPerDay.toFixed(2)}
                hint='ritmo da operação'
                cls='text-muted'
                tooltip='Média de trades fechadas por dia no período. Use pra detectar over-trading.'
            />
        </div>
        </>
    );
}

function KpiCard({ label, value, hint, cls, tooltip }) {
    return (
        <div className='col-6 col-md-4 col-xl'>
            <div className='card card-body border-0 shadow h-100 py-3' title={tooltip}>
                <small className='text-uppercase text-muted mb-1' style={{ fontSize: 11, letterSpacing: 0.5 }}>
                    {label}
                </small>
                <div className={`fw-bold ${cls}`} style={{ fontSize: 22, lineHeight: 1.1 }}>
                    {value}
                </div>
                {hint && (
                    <small className='text-muted mt-1' style={{ fontSize: 11 }}>{hint}</small>
                )}
            </div>
        </div>
    );
}

export default HeadlineMetrics;
